import React, { useState, useEffect, useRef } from 'react';
import { View, StyleSheet, Animated, Platform, Dimensions } from 'react-native';
import { useTheme } from '../contexts/ThemeContext';
import LoadingAnimation from './LoadingAnimation';
import CheckerPattern from './CheckerPattern';

const { width } = Dimensions.get('window');

const AppSplashScreen = ({ onFinish, duration = 2500 }) => {
  const fadeAnim = useRef(new Animated.Value(1)).current;
  const scaleAnim = useRef(new Animated.Value(0.95)).current;
  const [showSplash, setShowSplash] = useState(true);
  
  const { isDarkMode } = useTheme();
  
  const theme = {
    bg: isDarkMode ? '#0A0A0A' : '#FAFAFA',
  };

  useEffect(() => {
    // Subtle entrance animation
    Animated.spring(scaleAnim, {
      toValue: 1,
      tension: 80,
      friction: 10,
      useNativeDriver: true,
    }).start();

    // Exit animation after duration
    const exitTimer = setTimeout(() => {
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 0,
          duration: 300,
          useNativeDriver: true,
        }),
        Animated.timing(scaleAnim, {
          toValue: 1.05,
          duration: 300,
          useNativeDriver: true,
        }),
      ]).start(() => {
        setShowSplash(false);
        if (onFinish) {
          onFinish();
        }
      });
    }, duration);

    return () => clearTimeout(exitTimer);
  }, [duration]);

  if (!showSplash) {
    return null;
  }

  return (
    <Animated.View 
      style={[
        styles.container,
        {
          backgroundColor: theme.bg,
          opacity: fadeAnim,
          ...(Platform.OS === 'web' && { position: 'fixed' }),
        }
      ]}
      pointerEvents="none"
    >
      {/* Subtle checker pattern */}
      <CheckerPattern isDarkMode={isDarkMode} opacity={0.008} />

      {/* Just the animation - clean and minimal */}
      <Animated.View style={{ transform: [{ scale: scaleAnim }] }}>
        <LoadingAnimation size={Math.min(width * 1.0, 440)} />
      </Animated.View>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
  },
});

export default AppSplashScreen;
